import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import useAxiosPrivate from "@/api/hooks/useAxiosPrivate";
import { useFileDownload } from "@/components/Table/hooks";

import { ApiResources } from "../../../../../../api/resources";
import {
  DRIVERS_ACTIONS,
  GeneralDialogProps,
  ROUTES,
  TABLE_NAMES,
  useMediaQueries,
  VEHICLES_ACTIONS
} from "../../../../../../shared";
import DialogActions from "../../../../../../shared/components/buttons/DialogActions";
import useResponse from "../../../../../../shared/hooks/useResponse";
import Dialog from "../../../../../Dialog/Dialog";
import { DialogWidth } from "../../../../../Dialog/types/enums";
import ExportResourceDialogContent from "./ExportResourceDialogContent";
import {
  ExportResourceDialogProps,
  ResourceCounts,
  ResourceValue
} from "./types";

const ExportResourceDialog = ({
  route,
  tableName,
  isOpen,
  onClose
}: ExportResourceDialogProps & GeneralDialogProps) => {
  const { t } = useTranslation();

  const { toMd } = useMediaQueries();
  const { getData } = useAxiosPrivate();
  const { downloadFile } = useFileDownload();

  const { handleCommonError } = useResponse();

  const [isLoading, setIsLoading] = useState(false);
  const [isIncludeChecked, setIsIncludeChecked] = useState(false);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [resourceCounts, setResourceCounts] = useState<ResourceCounts | null>(
    null
  );

  const getResourceCounts = async () => {
    try {
      setIsCountLoading(true);
      const resourceUrl =
        route === ROUTES.Vehicles
          ? ApiResources.VehiclesCount
          : ApiResources.DriversCount;

      const { data } = await getData(resourceUrl);

      setResourceCounts(data);
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsCountLoading(false);
  };

  useEffect(() => {
    isOpen && getResourceCounts();
  }, [isOpen]);

  const resourceValues: { [key: string]: ResourceValue } = {
    [TABLE_NAMES.Vehicles]: {
      action: VEHICLES_ACTIONS.ExportVehicle,
      apiResource: ApiResources.VehiclesExport
    },
    [TABLE_NAMES.Drivers]: {
      action: DRIVERS_ACTIONS.ExportDriver,
      apiResource: ApiResources.DriversExport
    }
  };

  const resourceValueFromTableName = resourceValues[tableName];

  const handleCloseExportResourceDialog = () => {
    onClose(resourceValueFromTableName.action);
    resetChecked();
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      const queryString = `IncludeAllCompanies=${isIncludeChecked}`;

      const successMessage = t("Alert##successfully exported", {
        tableName
      });

      await downloadFile({
        noHeaders: true,
        successMessage,
        passUrl: `${import.meta.env.VITE_BASE_URL}${resourceValueFromTableName.apiResource}?${queryString}`,
        setIsLoading,
        onCloseDialog: handleCloseExportResourceDialog
      });
    } catch (error) {
      handleCommonError(error, t);
    }
    setIsLoading(false);
  };

  const resetChecked = () => {
    setIsIncludeChecked(false);
  };

  return (
    <Dialog
      title={t(`Dialog##export ${tableName}##title`)}
      description={t(`Dialog##export ${tableName}##description`)}
      data-testid={`export-${tableName}-dialog`}
      extendToMaxWidth={toMd}
      isTitleSeparator
      isActionsSeparator
      open={isOpen}
      close={handleCloseExportResourceDialog}
      submit={handleSubmit}
      width={toMd ? undefined : DialogWidth.MaxWidth}
      slotProps={{ transition: { onExited: () => resetChecked() } }}
      actions={
        <DialogActions
          onClose={handleCloseExportResourceDialog}
          isLoading={isLoading}
          isreadyToConfirm
          confirmationButtonText={t("Button##export")}
          confirmationButtonTestId={`confirm-export-${tableName}-button`}
          cancelButtonText={t("Button##close")}
          cancelButtonTestId={`close-export-${tableName}-dialog-button`}
        />
      }
    >
      <ExportResourceDialogContent
        tableName={tableName}
        isLoading={isCountLoading}
        resourceCounts={resourceCounts}
        isIncludeChecked={isIncludeChecked}
        setIsIncludeChecked={setIsIncludeChecked}
      />
    </Dialog>
  );
};

export default ExportResourceDialog;
